import { Injectable, InjectionToken } from '@angular/core';
import {
  IntervalType,
  OccInterval,
  OccTime,
  OccTimeSlot,
  OccTimeSlots,
  SlotListArray,
  TimeSlot,
  TimeSlots,
} from '@checkout/const';
import { CookieKeys } from '@core/const';
import { CookieService } from '@core/feature';
import { Converter, CxDatePipe } from '@spartacus/core';

@Injectable()
export class TimeSlotsNormalizer implements Converter<OccTimeSlots, TimeSlots> {
  isCheckoutV2Enabled = this.cookieService.hasKey(CookieKeys.ChekoutV2Enabled);

  constructor(
    private datePipe: CxDatePipe,
    private cookieService: CookieService,
  ) {}

  convert(source: OccTimeSlots, target?: TimeSlots): TimeSlots {
    if (this.isCheckoutV2Enabled) {
      return source as any;
    }

    if (target === undefined) {
      target = {
        slotListArray: [],
      };
    }

    if (!source?.slots?.length) {
      return target;
    }

    source.slots.forEach((occTimeSlot) => {
      let list: SlotListArray = target.slotListArray.find((slotList) => slotList.date === occTimeSlot.formattedDate);
      if (!list) {
        list = {
          date: occTimeSlot.formattedDate,
          groups: Object.values(IntervalType).reduce(
            (groups, intervalType) => {
              groups[intervalType] = [];

              return groups;
            },
            {} as SlotListArray['groups'],
          ),
        };
        target.slotListArray.push(list);
      }

      const slotIntervalType =
        Object.values(IntervalType).find((intervalType) => intervalType === occTimeSlot.intervalType) ||
        IntervalType.Standard;
      list.groups[slotIntervalType].push(...this.mapTimeSlot(occTimeSlot, slotIntervalType));
    });

    target.slotListArray.forEach((slotList) => {
      Object.values(slotList.groups).forEach((timeSlots) => {
        this.sortTimeSlots(timeSlots);
      });
    });

    return target;
  }

  private getDateTime({ hours, minutes }: OccTime): Date {
    const dateTime = new Date();
    dateTime.setHours(hours);
    dateTime.setMinutes(minutes);

    return dateTime;
  }

  private getTimeString(timeFrom: OccTime, timeTo: OccTime): string {
    const from = this.datePipe.transform(this.getDateTime(timeFrom), 'HH:mm');
    const to = this.datePipe.transform(this.getDateTime(timeTo), 'HH:mm');

    return `${from} - ${to}`;
  }

  private makeTitle(timeFrom: OccTime, timeTo: OccTime): string {
    if (timeFrom.hours === 8 && timeFrom.minutes === 0 && timeTo.hours === 14 && timeTo.minutes === 0) {
      return 'До обеда';
    }
    if (timeFrom.hours === 14 && timeFrom.minutes === 0 && timeTo.hours === 22 && timeTo.minutes === 0) {
      return 'После обеда';
    }

    return '';
  }

  private mapTimeSlot(occTimeSlot: OccTimeSlot, intervalType: IntervalType): TimeSlot[] {
    return occTimeSlot.intervals.map((interval: OccInterval): TimeSlot => {
      const timeString = this.getTimeString(interval.from, interval.to);

      return {
        intervalType,
        date: occTimeSlot.formattedDate,
        deliveryType: occTimeSlot.deliveryType,
        intervalDescription: occTimeSlot.intervalDescription,
        price: occTimeSlot.price,
        title: this.makeTitle(interval.from, interval.to),
        interval: timeString,
        intervalDisplay: timeString,
        transportIds: occTimeSlot.transportIds,
        services: [...(occTimeSlot.services || [])],
      };
    });
  }

  /**
   * Задумка в том, чтобы отсортировать уже готовые интервалы.
   * Сделано это не оптимально, чтобы в итоговом едином интерфейсе оставались только те данные,
   * что используются в приложении и при передаче в бекенд.
   * Также чтоб не уложнять нормалайзер.
   */
  private sortTimeSlots(timeSlots: TimeSlot[]): void {
    timeSlots.sort((slot1, slot2) => {
      const hours1 = Number(slot1.interval.slice(0, 2));
      const hours2 = Number(slot2.interval.slice(0, 2));

      return hours1 - hours2;
    });
  }
}

export const TIME_SLOTS_NORMALIZER = new InjectionToken<TimeSlotsNormalizer>('TimeSlotsResponseNormalizer');
